import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "@/core/store";
import { api } from "@/core/api-client";
import { useOrderCreate, useOrderUpdate } from "@/core/orders/hooks";
import {
  syncOrderStart,
  syncOrderSuccess,
  syncOrderError,
  rollbackOrderChanges,
  clearPendingRollback,
  clearOrder,
  detachOrder,
} from "@/core/orders/reducer";
import { selectCurrentOrder } from "@/core/orders/selectors";
import { selectBrandId, selectPointId, selectSource } from "@/core/app/selectors";
import {
  cartToItemsAdd,
  diffToPatch,
  isEmptyDelta,
  groupToItem,
  orderToOrderData,
} from "@/core/orders/map";
import {
  sendIframeMessage,
  shouldCreateOrder,
  shouldUpdateOrder,
} from "@/core/orders/utils";
import { isOrderNotFound } from "@/core/orders/guards";
import type { RestOrder } from "@/core/orders/schemaTypes";
import type { CurrentOrderState } from "@/core/orders/types";

/** Пауза между последней правкой корзины и запросом. Была debounce(300) в саге. */
const SYNC_DEBOUNCE_MS = 300;

export interface CartSync {
  /**
   * Синхронизировать немедленно, отменив незавершённый дебаунс.
   *
   * Отдаёт заказ с сервера — кнопке предзаказа нужен его идентификатор, —
   * либо null, когда синхронизировать было нечего и заказа ещё нет.
   * Ошибку мутации БРОСАЕТ: по ней кнопка шлёт PREORDER_ERROR.
   */
  flush: () => Promise<RestOrder | null>;
}

/**
 * Синхронизация корзины с сервером. Заменяет ordersSaga.
 *
 * Вход дебаунса — флаг pendingChanges, а НЕ объект currentOrder: успех
 * заменяет объект целиком (syncOrderSuccess), а shouldUpdateOrder истинен при
 * любом непустом id, поэтому подписка на объект замкнулась бы в петлю пустых
 * PATCH.
 *
 * Серверный состав для дельты берётся из кэша TanStack, а не из
 * currentOrder.items: туда syncOrderSuccess кладёт ответ сервера, и сравнение
 * шло бы объекта с самим собой — дельта всегда пустая, PATCH не уходит.
 *
 * Контракт отката двусторонний: reducer снимает снимок сам, но только при
 * pendingRollback === null. Поэтому КАЖДЫЙ ранний выход обязан звать
 * clearPendingRollback — иначе протухший снимок переживёт пропущенный цикл и
 * следующая ошибка откатит корзину на состояние многократной давности.
 */
export function useCartSync(): CartSync {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const currentOrder = useAppSelector(selectCurrentOrder);
  const brandId = useAppSelector(selectBrandId);
  const pointId = useAppSelector(selectPointId);
  const source = useAppSelector(selectSource);
  const pendingChanges = Boolean(currentOrder.pendingChanges);

  const create = useOrderCreate();
  const update = useOrderUpdate();

  // Синхронизация читает состояние на момент СВОЕГО запуска, а не на момент
  // постановки таймера: между ними успевает пройти ещё десяток правок.
  const cartRef = useRef<CurrentOrderState>(currentOrder);
  cartRef.current = currentOrder;
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const pointRef = useRef(pointId);
  pointRef.current = pointId;
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const isCreatingRef = useRef(false);
  // Один цикл синхронизации за раз: правка внутри окна полёта перезаводит
  // таймер, и без гарда два PATCH по одному заказу ушли бы параллельно —
  // сервер применил бы их в непредсказуемом порядке.
  const isSyncingRef = useRef(false);
  // Промис цикла в полёте: flush обязан его дождаться, иначе кнопка предзаказа,
  // нажатая пока летит POST, получит null и соврёт пользователю про ошибку.
  const inFlightRef = useRef<Promise<RestOrder | null> | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const orderQueryOptions = useCallback(
    (orderId: string, brand: string) =>
      api.queryOptions("get", "/orders/{id}", {
        params: { path: { id: orderId }, query: { brandId: brand } },
      }),
    []
  );

  /** Ответ мутации — свежайший серверный состав; следующая дельта считается от него. */
  const cacheOrder = useCallback(
    (order: RestOrder, brand: string) => {
      queryClient.setQueryData(orderQueryOptions(order.id, brand).queryKey, order);
    },
    [queryClient, orderQueryOptions]
  );

  /**
   * Фоновое «состав поменялся» — только в режиме предзаказа, как в саге:
   * в qr и delivery наружу сегодня ничего не уходит, и фаза не должна
   * заводить новый исходящий трафик.
   */
  const notifyUpdated = useCallback((order: RestOrder) => {
    if (sourceRef.current !== "preorder") return;
    sendIframeMessage(
      "PREORDER_UPDATED",
      sourceRef.current,
      orderToOrderData(order)
    );
  }, []);

  /**
   * Один цикл синхронизации. Отдаёт заказ сервера либо null, когда слать было
   * нечего; ошибку мутации бросает наружу — её ловит либо дебаунс (там она
   * уже отражена в сторе и снекбаре), либо кнопка предзаказа.
   */
  const sync = useCallback(async (): Promise<RestOrder | null> => {
    const cart = cartRef.current;
    const brand = brandRef.current;
    const point = pointRef.current;

    if (!brand || !point) {
      dispatch(clearPendingRollback());
      return null;
    }

    if (!cart.pendingChanges) {
      dispatch(clearPendingRollback());
      return null;
    }

    // Второй черновик не заводим: пока POST в полёте, id ещё не известен, и
    // shouldCreateOrder остаётся истинным — без гарда каждая правка
    // порождала бы отдельный заказ.
    if (isCreatingRef.current || isSyncingRef.current) {
      dispatch(clearPendingRollback());
      return null;
    }

    // «Запрос ушёл»: снимает pendingChanges (состав уже забран) и поднимает
    // isSyncing. Читателя у флага в UI сейчас нет — useCart его больше не
    // отдаёт (подписка перерисовывала карточки списка на каждом цикле);
    // понадобится спиннер — компонент подпишется на selectIsSyncing сам.
    isSyncingRef.current = true;
    dispatch(syncOrderStart());

    try {
      if (shouldCreateOrder(cart)) {
        isCreatingRef.current = true;
        try {
          const order = await create.mutateAsync({
            body: {
              brandId: brand,
              pointId: point,
              itemsAdd: cartToItemsAdd(cart),
              // comment имеет minLength: 1 — пустая строка даёт 400.
              comment: cart.comment || undefined,
              personsNumber: cart.personsNumber ?? undefined,
            },
          });
          cacheOrder(order, brand);
          dispatch(syncOrderSuccess(order));
          notifyUpdated(order);
          return order;
        } finally {
          isCreatingRef.current = false;
        }
      }

      if (!shouldUpdateOrder(cart) || !cart.id) {
        dispatch(clearPendingRollback());
        return null;
      }

      // Заказ известен, а его состава в кэше нет — после перезагрузки, пока
      // восстановление не отработало. Дозапрашиваем: без серверного состава
      // дельта посчиталась бы от пустоты и продублировала бы весь заказ.
      const options = orderQueryOptions(cart.id, brand);
      const serverOrder =
        queryClient.getQueryData<RestOrder>(options.queryKey) ??
        (await queryClient.fetchQuery(options));

      const delta = diffToPatch(serverOrder.items.map(groupToItem), cart.items);
      const cartComment = cart.comment || "";
      const commentChanged = (serverOrder.comment ?? "") !== cartComment;

      if (isEmptyDelta(delta) && !commentChanged) {
        dispatch(clearPendingRollback());
        // Слать было нечего, но заказ существует: кнопке предзаказа нужен
        // именно он, а не пустота.
        return serverOrder;
      }

      const order = await update.mutateAsync({
        params: { path: { id: cart.id } },
        body: {
          // brandId у PATCH — в теле, не в строке запроса.
          brandId: brand,
          ...delta,
          // Стёртый комментарий уходит как null, а НЕ undefined:
          // JSON.stringify выбрасывает undefined-ключи, и сервер оставил бы
          // прежний текст. Пустая строка недопустима — minLength: 1.
          ...(commentChanged ? { comment: cartComment || null } : {}),
        },
      });
      cacheOrder(order, brand);
      dispatch(syncOrderSuccess(order));
      notifyUpdated(order);
      return order;
    } catch (error) {
      dispatch(
        syncOrderError(
          error instanceof Error ? error.message : "Ошибка синхронизации заказа"
        )
      );
      // Откат: reducer читает state.pendingRollback и восстанавливает сам.
      dispatch(rollbackOrderChanges());

      // 404 на записи — «заказа нет либо он чужой»: так отвечает сервер после
      // ротации анонимной сессии. Ссылку надо снять, иначе каждая следующая
      // правка повторяет тот же 404 и молча откатывается: снекбар на этот код
      // заглушён намеренно, и пользователь не увидит ничего.
      if (isOrderNotFound(error)) {
        dispatch(detachOrder());
      }
      // Снекбар показывает MutationCache (core/error-reporting.ts); наружу
      // ошибка уходит дальше — по ней кнопка шлёт PREORDER_ERROR.
      throw error;
    } finally {
      isSyncingRef.current = false;
    }
  }, [
    dispatch,
    queryClient,
    create,
    update,
    cacheOrder,
    orderQueryOptions,
    notifyUpdated,
  ]);

  /** Обёртка, публикующая промис текущего цикла для flush. */
  const runSync = useCallback((): Promise<RestOrder | null> => {
    const running = inFlightRef.current;
    if (running) return running;

    const promise = sync().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, [sync]);

  const syncRef = useRef(runSync);
  syncRef.current = runSync;

  // Дебаунс. Зависимость — флаг, а не объект корзины: см. шапку файла.
  useEffect(() => {
    if (!pendingChanges) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Ошибка фонового цикла уже отражена в сторе и снекбаре — глотаем,
      // иначе получим unhandled rejection.
      void syncRef.current().catch(() => {});
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // items и comment в зависимостях нужны, чтобы правка ВНУТРИ окна
    // дебаунса перезаводила таймер: pendingChanges при этом не меняется.
  }, [pendingChanges, currentOrder.items, currentOrder.comment]);

  const flush = useCallback(async (): Promise<RestOrder | null> => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Цикл уже в полёте — ждём его вместо того, чтобы выйти по гарду с null.
    const running = inFlightRef.current;
    const synced = running ? await running : await syncRef.current();
    if (synced) return synced;

    // Синхронизировать было нечего — обычный случай для кнопки предзаказа:
    // пользователь добавил позиции, дебаунс отработал сам, и pendingChanges
    // уже снят. Заказ при этом существует, и хосту нужен именно он.
    const cart = cartRef.current;
    const brand = brandRef.current;
    if (!cart.id || !brand) return null;

    const options = orderQueryOptions(cart.id, brand);
    return (
      queryClient.getQueryData<RestOrder>(options.queryKey) ??
      (await queryClient.fetchQuery(options))
    );
  }, [queryClient, orderQueryOptions]);

  // Гарды смены точки и бренда. Раньше жили сагами handlePointIdChangeSaga и
  // handleBrandIdChangeSaga; логика та же, включая два молчания: null сменой
  // не считается, и первое появление значения — не смена, а первый визит.
  //
  // Сравнивается ПРЕЖНЕЕ значение app-слайса, а не currentOrder: тот получает
  // точку и бренд только с ответом сервера, поэтому корзина, набранная
  // локально и ни разу не синхронизированная, пережила бы смену точки — и
  // уехала бы POST'ом на новую точку с товарами старой.
  //
  // Отличие от саги: вместе с clearOrder идёт detachOrder. clearOrder при
  // непустом id СОХРАНЯЕТ и id, и persistedOrderId, то есть следующий тик
  // послал бы PATCH по заказу прежней точки.
  const prevBrandRef = useRef(brandId);
  const prevPointRef = useRef(pointId);

  useEffect(() => {
    const previous = prevBrandRef.current;
    prevBrandRef.current = brandId;
    if (!brandId || !previous || previous === brandId) return;

    dispatch(clearOrder());
    dispatch(detachOrder());
  }, [brandId, dispatch]);

  useEffect(() => {
    const previous = prevPointRef.current;
    prevPointRef.current = pointId;
    if (!pointId || !previous || previous === pointId) return;

    dispatch(clearOrder());
    dispatch(detachOrder());
  }, [pointId, dispatch]);

  return { flush };
}

export default useCartSync;
